/**
 * GraphQL Login Token Hooks
 * Feature: 076-unified-login-tokens
 *
 * Hooks for PIN-based authentication flows:
 * - Request password reset with PIN
 * - Reset password with PIN verification
 * - Request login link (passwordless)
 * - Exchange login token for JWT (passwordless login)
 */

import { useMutation, gql } from '@apollo/client';
import { apolloClient } from '../../graphql/client';
import logger from '../../utils/logger';

// GraphQL Mutations
const REQUEST_PASSWORD_RESET_WITH_PIN_MUTATION = gql`
  mutation RequestPasswordResetWithPin($input: ForgotPasswordInput!) {
    requestPasswordResetWithPin(input: $input) {
      success
      message
    }
  }
`;

const RESET_PASSWORD_WITH_PIN_MUTATION = gql`
  mutation ResetPasswordWithPin($input: ResetPasswordWithPinInput!) {
    resetPasswordWithPin(input: $input) {
      success
      message
    }
  }
`;

const REQUEST_LOGIN_LINK_MUTATION = gql`
  mutation RequestLoginLink($input: RequestLoginLinkInput!) {
    requestLoginLink(input: $input) {
      success
      message
    }
  }
`;

const EXCHANGE_LOGIN_TOKEN_MUTATION = gql`
  mutation ExchangeLoginToken($input: ExchangeLoginTokenInput!) {
    exchangeLoginToken(input: $input) {
      user {
        id
        globalId
        email
        username
        slug
        displayName
        createdAt
        updatedAt
      }
      token
    }
  }
`;

// Types
interface User {
  id: string;
  globalId: string;
  email: string;
  username: string;
  slug: string;
  displayName: string;
  createdAt: string;
  updatedAt: string;
}

interface SuccessPayload {
  success: boolean;
  message: string;
}

interface AuthPayload {
  token: string;
  user: User;
}

/**
 * Hook for requesting a password reset email with PIN
 *
 * Sends an email with a link AND a PIN. The user must enter both
 * on the reset page to change their password.
 */
export function useRequestPasswordResetWithPinGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { requestPasswordResetWithPin: SuccessPayload },
    { input: { email: string } }
  >(REQUEST_PASSWORD_RESET_WITH_PIN_MUTATION);

  const requestReset = async (email: string) => {
    logger.info('Auth', 'Requesting password reset with PIN via GraphQL');

    const result = await mutate({ variables: { input: { email } } });

    if (result.data?.requestPasswordResetWithPin.success) {
      logger.info('Auth', 'Password reset request sent');
    }

    return result.data?.requestPasswordResetWithPin;
  };

  return {
    requestReset,
    data: data?.requestPasswordResetWithPin,
    loading,
    error,
  };
}

/**
 * Hook for resetting password with nonce + PIN verification
 *
 * Validates the nonce (from URL) and PIN (from email), then
 * updates the user's password.
 */
export function useResetPasswordWithPinGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { resetPasswordWithPin: SuccessPayload },
    { input: { nonce: string; pin: string; newPassword: string } }
  >(RESET_PASSWORD_WITH_PIN_MUTATION);

  const resetPassword = async (nonce: string, pin: string, newPassword: string) => {
    logger.info('Auth', 'Resetting password with PIN via GraphQL');

    const result = await mutate({
      variables: { input: { nonce, pin, newPassword } },
    });

    if (result.data?.resetPasswordWithPin.success) {
      logger.info('Auth', 'Password reset successful');
    }

    return result.data?.resetPasswordWithPin;
  };

  return {
    resetPassword,
    data: data?.resetPasswordWithPin,
    loading,
    error,
  };
}

/**
 * Hook for requesting a passwordless login link
 *
 * Sends an email with a link AND a PIN. The user must enter both
 * on the login page to authenticate without a password.
 */
export function useRequestLoginLinkGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { requestLoginLink: SuccessPayload },
    { input: { email: string } }
  >(REQUEST_LOGIN_LINK_MUTATION);

  const requestLink = async (email: string) => {
    logger.info('Auth', 'Requesting login link via GraphQL');

    const result = await mutate({ variables: { input: { email } } });

    if (result.data?.requestLoginLink.success) {
      logger.info('Auth', 'Login link request sent');
    }

    return result.data?.requestLoginLink;
  };

  return {
    requestLink,
    data: data?.requestLoginLink,
    loading,
    error,
  };
}

/**
 * Hook for exchanging a login token for a JWT (passwordless login)
 *
 * Validates the nonce (from URL) and PIN (from email), then
 * returns a JWT for authentication.
 */
export function useExchangeLoginTokenGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { exchangeLoginToken: AuthPayload },
    { input: { nonce: string; pin: string } }
  >(EXCHANGE_LOGIN_TOKEN_MUTATION);

  const exchangeToken = async (nonce: string, pin: string) => {
    logger.info('Auth', 'Exchanging login token via GraphQL');

    const result = await mutate({ variables: { input: { nonce, pin } } });

    if (result.data?.exchangeLoginToken) {
      const { token, user } = result.data.exchangeLoginToken;

      // Store token and user in localStorage (matching existing auth pattern)
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      // Reset Apollo cache on login (matches useLoginGraphQL pattern)
      await apolloClient.resetStore();

      logger.info('Auth', 'Login via link successful', { userId: user.id });
    }

    return result.data?.exchangeLoginToken;
  };

  return {
    exchangeToken,
    data: data?.exchangeLoginToken,
    loading,
    error,
  };
}
